import { gql } from '@apollo/client';
import { Box, VStack } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import NextLink from 'next/link';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { useCommentActions } from '@/components/Post/hooks/useCommentActions';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { usePostHref } from '@/hooks/usePostHref';

import { Poll_PollV2Fragment } from './graphql-types/Poll_PollV2Fragment';

import { Comments } from '../Comments';
import { CreateComment, CreateComment_postFragment } from '../CreateComment';
import { LikeCommentFooter } from '../LikeCommentFooter';
import { PostHeader } from '../PostHeader';
import { Tags } from '../Tags';
import { Title } from '../Title';
import { Options } from './components/Options';
import { VoteCount } from './components/VoteCount';

type PostHeaderProps = React.ComponentProps<typeof PostHeader>;

export const Poll: React.FC<{
  poll: Poll_PollV2Fragment;
  options?: PostHeaderProps['options'] & { isExpandedByDefault?: boolean };
}> & {
  pollFragment: DocumentNode;
} = ({ poll, options }) => {
  const postHref = usePostHref(poll._id);

  const {
    ref,
    focusAndScrollToRef,
    expandOrFocusAndScrollToRef,
    isActive,
    setIsActive,
    ...expandProps
  } = useCommentActions({
    post: poll,
    isExpandedByDefault: options?.isExpandedByDefault,
  });

  return (
    <Box p="4">
      <PostHeader post={poll} mb="4" options={options} />
      <NextLink href={postHref}>
        <Title>{poll.pollTitle}</Title>
      </NextLink>
      {poll.descriptionContentState && (
        <AbbreviateRedraft
          options={{ defaultIsOpen: options?.isExpandedByDefault }}
          rawDraftContentState={poll.descriptionContentState}
          noOfCharacters={500}
        />
      )}
      <VStack spacing="4" width="full" align="start" mt="6">
        <Options poll={poll} />
        <VoteCount poll={poll} />
        <Tags post={poll} />
        <LikeCommentFooter
          post={poll}
          onClickComment={focusAndScrollToRef}
          onClickCommentCount={expandOrFocusAndScrollToRef}
        />
        <Comments post={poll} expandProps={expandProps} />
        <CreateComment
          post={poll}
          ref={ref}
          isActive={isActive}
          setIsActive={setIsActive}
        />
      </VStack>
    </Box>
  );
};

Poll.pollFragment = gql`
  fragment Poll_PollV2Fragment on PollV2 {
    _id
    pollTitle: title
    descriptionContentState {
      ...RawDraftContentStateFragment
    }
    ...Options_PollV2Fragment
    ...VoteCount_PollV2Fragment
    ...PostHeader_PostFragment
    ...Tags_PostFragment
    ...LikeCommentFooter_PostFragment
    ...Comments_PostFragment
    ...CreateComment_PostFragment
    ...useCommentActions_PostFragment
  }
  ${Options.pollFragment}
  ${VoteCount.pollFragment}
  ${PostHeader.postFragment}
  ${Tags.postFragment}
  ${LikeCommentFooter.postFragment}
  ${Comments.postFragment}
  ${CreateComment_postFragment}
  ${useCommentActions.postFragment}
  ${RawDraftContentStateFragmentAST}
`;
